import { useState, useEffect } from "react";

/**
 * Address autocomplete using OpenStreetMap Nominatim (free, no API key).
 * Props:
 *   value       - controlled string value
 *   onChange    - called with new string value on every keystroke
 *   onSelect    - called with { display_name, lat, lon } when user picks a suggestion
 *   placeholder - input placeholder text
 *   className   - extra classes on the wrapper div
 *   inputClassName - extra classes on the input
 *   data-testid - forwarded to the input element
 */
const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Strasse, Hausnummer, Stadt, PLZ",
  className = "",
  inputClassName = "",
  "data-testid": testId = "address-autocomplete-input",
}) => {
  const [validation, setValidation] = useState({
    isValidating: false,
    isValid: false,
    suggestions: [],
    error: null,
  });
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!value || value.length < 5) {
        setValidation({ isValidating: false, isValid: false, suggestions: [], error: null });
        setShowSuggestions(false);
        return;
      }
      setValidation((prev) => ({ ...prev, isValidating: true, error: null }));
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&addressdetails=1&limit=5&countrycodes=ch,de,at`,
          { headers: { "Accept-Language": "de,en" } }
        );
        const data = await res.json();
        if (data.length > 0) {
          setValidation({
            isValidating: false,
            isValid: true,
            error: null,
            suggestions: data.map((item) => ({
              display_name: item.display_name,
              lat: item.lat,
              lon: item.lon,
            })),
          });
          setShowSuggestions(true);
        } else {
          setValidation({
            isValidating: false,
            isValid: false,
            suggestions: [],
            error: "Adresse nicht gefunden.",
          });
          setShowSuggestions(false);
        }
      } catch {
        setValidation({
          isValidating: false,
          isValid: false,
          suggestions: [],
          error: "Adresse konnte nicht geprüft werden.",
        });
        setShowSuggestions(false);
      }
    };

    const timer = setTimeout(validate, 1000);
    return () => clearTimeout(timer);
  }, [value]);

  const handleSelect = (suggestion) => {
    onChange(suggestion.display_name);
    if (onSelect) onSelect(suggestion);
    setShowSuggestions(false);
  };

  const borderClass = validation.isValid
    ? "border-green-500 focus:ring-green-200"
    : validation.error
    ? "border-red-400 focus:ring-red-200"
    : "border-gray-300 focus:ring-blue-200";

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className={`w-full border rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 transition-colors ${borderClass} ${inputClassName}`}
          autoComplete="off"
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onFocus={() => validation.suggestions.length > 0 && setShowSuggestions(true)}
        />
        {/* Status icon */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {validation.isValidating && (
            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
          )}
          {!validation.isValidating && validation.isValid && (
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {!validation.isValidating && validation.error && (
            <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </div>
      </div>

      {/* Error message */}
      {validation.error && (
        <p className="text-xs text-red-600 mt-1">{validation.error}</p>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && validation.suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 border border-gray-200 rounded-lg bg-white shadow-lg overflow-hidden">
          {validation.suggestions.slice(0, 4).map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={() => handleSelect(s)}
              className="w-full text-left text-xs px-3 py-2.5 hover:bg-blue-50 border-b border-gray-100 last:border-0 flex items-start gap-2 transition-colors"
              data-testid={`address-suggestion-${i}`}
            >
              <svg className="w-3.5 h-3.5 text-[#0044CC] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-gray-700 leading-relaxed">{s.display_name}</span>
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400 mt-1">
        Adresse eingeben — wird automatisch verifiziert.
      </p>
    </div>
  );
};

export default AddressAutocomplete;
